/**
 * LanguageSwitcher — ES / EN / FR toggle with real flags.
 *
 * Accessibility:
 *  - `role="group"` + `aria-label="Idioma"` so screen readers announce the
 *    purpose of the control set.
 *  - Each button has `aria-pressed` reflecting selection state.
 *  - Touch targets ≥ 44×44 px (WCAG 2.5.5).
 *  - Visible focus ring (`focus-visible:ring-gold`) for keyboard users.
 *  - Flags are decorative; the visible language label (ES/EN/FR) carries the
 *    accessible name, and we add an SR-only "Cambiar a Español/English/Français"
 *    so vision-impaired users get a clear action description.
 *
 * Variants:
 *   - `inline` (default): horizontal row used in the desktop navbar.
 *   - `stack`: larger, full-width row used inside the mobile hamburger overlay.
 */
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";

/* ============ Inline SVG flags ============ */
/* Each flag is decorative (aria-hidden) and uses a rounded rect mask so the
   borders match the gold-bordered button container. */

function FlagES({ className = "" }) {
    return (
        <svg
            viewBox="0 0 60 40"
            className={className}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="60" height="40" fill="#AA151B" />
            <rect y="10" width="60" height="20" fill="#F1BF00" />
        </svg>
    );
}

function FlagEN({ className = "" }) {
    // Simplified Union Jack — recognisable at small sizes.
    return (
        <svg
            viewBox="0 0 60 40"
            className={className}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="60" height="40" fill="#012169" />
            {/* Diagonals (white background then red overlay) */}
            <path
                d="M0,0 L60,40 M60,0 L0,40"
                stroke="#FFFFFF"
                strokeWidth="6"
            />
            <path
                d="M0,0 L60,40 M60,0 L0,40"
                stroke="#C8102E"
                strokeWidth="2.5"
            />
            {/* Cross */}
            <path d="M30,0 V40 M0,20 H60" stroke="#FFFFFF" strokeWidth="10" />
            <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
        </svg>
    );
}

function FlagFR({ className = "" }) {
    return (
        <svg
            viewBox="0 0 60 40"
            className={className}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="20" height="40" fill="#0055A4" />
            <rect x="20" width="20" height="40" fill="#FFFFFF" />
            <rect x="40" width="20" height="40" fill="#EF4135" />
        </svg>
    );
}

const FLAG_MAP = { es: FlagES, en: FlagEN, fr: FlagFR };
const FULL_NAME = { es: "Español", en: "English", fr: "Français" };

export default function LanguageSwitcher({ variant = "inline" }) {
    const { i18n, t } = useTranslation();
    const current = (i18n.language || "es").slice(0, 2);

    const change = (code) => {
        if (code !== current) i18n.changeLanguage(code);
    };

    const isStack = variant === "stack";

    return (
        <div
            className={
                isStack
                    ? "flex items-center justify-center gap-3"
                    : "flex items-center gap-1.5"
            }
            role="group"
            aria-label={t("lang.label")}
            data-testid={isStack ? "lang-switcher-mobile" : "lang-switcher"}
        >
            {SUPPORTED_LANGUAGES.map((l) => {
                const Flag = FLAG_MAP[l.code];
                const active = current === l.code;
                return (
                    <button
                        key={l.code}
                        type="button"
                        onClick={() => change(l.code)}
                        aria-pressed={active}
                        aria-label={`${t("lang.switch")} ${FULL_NAME[l.code]}`}
                        data-testid={`lang-${l.code}`}
                        title={`${t("lang.switch")} → ${FULL_NAME[l.code]}`}
                        className={`group relative inline-flex items-center justify-center gap-2 border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${
                            isStack
                                ? "px-3 py-2 min-h-[44px] min-w-[44px]"
                                : "w-9 h-9"
                        } ${
                            active
                                ? "border-gold bg-gold/10 text-gold"
                                : "border-gold/25 text-stone hover:border-gold/70 hover:text-ivory"
                        }`}
                    >
                        <Flag
                            className={`block ${
                                isStack ? "w-7 h-5" : "w-5 h-[14px]"
                            } shrink-0 border border-black/10 rounded-[1px] overflow-hidden`}
                        />
                        {isStack && (
                            <span className="font-editorial italic tracking-[0.18em] uppercase text-sm">
                                {l.short}
                            </span>
                        )}
                        {/* Screen-reader only — full action label */}
                        <span className="sr-only">
                            {t("lang.switch")} {FULL_NAME[l.code]}
                            {active ? ` (${t("lang.label")} actual)` : ""}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
